import React from 'react';
import { toast } from 'sonner';

import { Switch } from '@/components/ui/switch';
import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import { SettingDisplayCard } from '@/sections/settings/components';

interface NotificationPreferencesSubsectionProps {
  me: string;
}

const PREFERENCE_CARDS: ReadonlyArray<{
  settingKey: string;
  title: string;
  description: string;
  testIdPrefix: string;
  praetorianOnly?: boolean;
}> = [
  {
    settingKey: 'notification-threat-emails',
    title: 'Security Advisory Emails',
    description: 'Receive email alerts for critical CVE and threat advisories',
    testIdPrefix: 'threat-emails',
  },
  {
    settingKey: 'notification-risk-alerts',
    title: 'Risk Vulnerability Alerts',
    description: 'Receive email alerts when new vulnerabilities are discovered',
    testIdPrefix: 'risk-alerts',
  },
  {
    settingKey: 'notification-health-report',
    title: 'Health Report Emails',
    description: 'Receive periodic security health reports for your account',
    testIdPrefix: 'health-report-emails',
  },
  {
    settingKey: 'notification-collaborator-invites',
    title: 'Collaborator Invite Emails',
    description: 'Receive email notifications when added to a tenant',
    testIdPrefix: 'collaborator-invites',
  },
  {
    settingKey: 'notification-engagement-digest',
    title: 'Engagement Digest & Checkpoint Emails',
    description: 'Receive engagement digest and checkpoint alert emails',
    testIdPrefix: 'engagement-digest',
    praetorianOnly: true,
  },
];

export const NotificationPreferencesSubsection: React.FC<
  NotificationPreferencesSubsectionProps
> = ({ me }) => {
  const { data: settings, refetch: refetchSettings } = useMy(
    {
      resource: 'setting',
    },
    {
      email: me,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const { mutateAsync: updateSetting } = useUpdateSetting(me);

  const isPraetorian = me.endsWith('@praetorian.com');

  const handleToggle = async (name: string, checked: boolean) => {
    try {
      await updateSetting({
        name,
        value: String(checked),
        silent: true,
      });
      await refetchSettings();
      toast.success('Notification preference updated');
    } catch {
      toast.error(
        'Failed to update notification preference. Please try again.'
      );
    }
  };

  return (
    <div
      data-tutorial="notification-preferences"
      className="flex flex-col gap-2"
    >
      <h3
        className="text-chariot-text-primary mt-6 text-base font-bold"
        data-testid="notification-preferences-subsection-header"
      >
        Notification Preferences
      </h3>

      {PREFERENCE_CARDS.map(card => {
        if (card.praetorianOnly && !isPraetorian) return null;

        const isEnabled =
          settings?.find(s => s.name === card.settingKey)?.value !== 'false';

        return (
          <SettingDisplayCard
            key={card.settingKey}
            data-testid={`${card.testIdPrefix}-card`}
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <h4 className="font-bold">{card.title}</h4>
              </div>
              <span className="text-chariot-text-secondary text-xs">
                {card.description}
              </span>
            </div>
            <div className="absolute top-1/2 right-4 -translate-y-1/2">
              <Switch
                checked={isEnabled}
                onCheckedChange={checked =>
                  handleToggle(card.settingKey, checked)
                }
                data-testid={`${card.testIdPrefix}-switch`}
              />
            </div>
          </SettingDisplayCard>
        );
      })}
    </div>
  );
};
